import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, { type: 'default' | 'rdm', productData?: any, activeCurrency?: string, dopToUsdRate?: number }>} */
  #cachedMetadata = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.#cachedMetadata.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    this.#cachedContent.clear();
    this.#cachedMetadata.clear();

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);
    let contentMetadata = this.#cachedMetadata.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const defaultGridElement = html.querySelector('[data-product-grid-content]');
        const rdmPayload = this.#extractRdmQuickAddContent(html);

        const gridElement = defaultGridElement || rdmPayload?.element;
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
          contentMetadata = defaultGridElement
            ? { type: 'default' }
            : {
                type: 'rdm',
                productData: rdmPayload?.productData,
                activeCurrency: rdmPayload?.activeCurrency,
                dopToUsdRate: rdmPayload?.dopToUsdRate,
              };
          this.#cachedMetadata.set(currentUrl, contentMetadata);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      const isDefaultQuickAdd = contentMetadata?.type !== 'rdm';

      await this.updateQuickAddModal(freshContent, {
        syncVariantSelection: isDefaultQuickAdd,
        rdmMode: !isDefaultQuickAdd,
      });

      if (isDefaultQuickAdd) {
        this.#updateVariantPicker(productGrid);
      } else {
        this.#initializeRdmQuickAdd();
      }
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details, .rdm-product__info');
    const productMedia = dialogComponent.querySelector('.product-information__media, .rdm-product__gallery');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   * @param {{ syncVariantSelection?: boolean }} [options]
   */
  async updateQuickAddModal(productGrid, options = {}) {
    const modalContent = document.getElementById('quick-add-modal-content');
    const { syncVariantSelection = true, rdmMode = false } = options;

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    const isRdmQuickAdd =
      rdmMode ||
      Boolean(
        modalContent.querySelector('.rdm-quick-add-content, .rdm-quick-add-media, .rdm-quick-add-info'),
      );
    modalContent.toggleAttribute('data-rdm-quick-add', isRdmQuickAdd);
    const dialogComponent = document.getElementById('quick-add-dialog');
    dialogComponent?.toggleAttribute('data-rdm-quick-add', isRdmQuickAdd);

    if (syncVariantSelection) {
      this.#syncVariantSelection(modalContent);
    }
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  /**
   * Extracts quick-add compatible content for the custom RDM PDP.
   * @param {Document} html
   * @returns {{ element: Element, productData: any, activeCurrency: string, dopToUsdRate: number } | null}
   */
  #extractRdmQuickAddContent(html) {
    const section = html.querySelector('[id^="rdm-product-"]');
    if (!(section instanceof Element)) return null;

    const title = section.querySelector('.rdm-product__title');
    const price = section.querySelector('.rdm-product__price');
    const usdHint = section.querySelector('[data-rdm-usd-hint]');
    const description = section.querySelector('.rdm-product__description');
    const form = section.querySelector('.rdm-product__form');
    const mainImage = section.querySelector('.rdm-product__main-image');

    if (!(form instanceof HTMLFormElement)) return null;

    const shell = document.createElement('div');
    shell.className = 'rdm-quick-add-content';

    const mediaCol = document.createElement('div');
    mediaCol.className = 'rdm-quick-add-media';

    const imageWrap = document.createElement('div');
    imageWrap.className = 'rdm-product__main-media';
    if (mainImage instanceof HTMLImageElement) {
      imageWrap.appendChild(mainImage.cloneNode(true));
    }
    mediaCol.appendChild(imageWrap);

    const infoCol = document.createElement('div');
    infoCol.className = 'rdm-quick-add-info';

    if (title instanceof HTMLElement) {
      infoCol.appendChild(title.cloneNode(true));
    }
    if (price instanceof HTMLElement) {
      infoCol.appendChild(price.cloneNode(true));
    }
    if (usdHint instanceof HTMLElement) {
      infoCol.appendChild(usdHint.cloneNode(true));
    }
    if (description instanceof HTMLElement) {
      const descClone = /** @type {HTMLElement} */ (description.cloneNode(true));
      descClone.classList.add('rdm-quick-add-description');
      infoCol.appendChild(descClone);
    }

    const formClone = /** @type {HTMLFormElement} */ (form.cloneNode(true));
    const heveaGroups = formClone.querySelector('.rdm-hevea');
    if (heveaGroups) {
      heveaGroups.classList.add('rdm-hevea--table');
    }
    infoCol.appendChild(formClone);

    shell.appendChild(mediaCol);
    shell.appendChild(infoCol);

    const sectionId = section?.id?.replace('rdm-product-', '') || '';
    const productJson = sectionId ? html.getElementById(`rdm-product-json-${sectionId}`) : null;

    let productData = null;
    if (productJson?.textContent) {
      try {
        productData = JSON.parse(productJson.textContent);
      } catch (_error) {
        productData = null;
      }
    }

    const hasDopHint = Boolean(shell.querySelector('[data-rdm-usd-hint]'));
    return {
      element: shell,
      productData,
      activeCurrency: hasDopHint ? 'DOP' : 'EUR',
      dopToUsdRate: 0.017,
    };
  }

  /**
   * Initializes interactions for the custom RDM quick-add content.
   */
  #initializeRdmQuickAdd() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const rdmContent = /** @type {HTMLElement | null} */ (
      modalContent.querySelector('.rdm-quick-add-content') ||
      (modalContent.querySelector('.rdm-quick-add-media, .rdm-quick-add-info') ? modalContent : null)
    );
    if (!(rdmContent instanceof HTMLElement)) return;

    const currentUrl = this.productPageUrl;
    const metadata = this.#cachedMetadata.get(currentUrl);
    const productData = metadata?.productData;

    const formEl = /** @type {HTMLFormElement | null} */ (rdmContent.querySelector('.rdm-product__form'));
    if (!formEl) return;

    const optionEls = Array.from(formEl.querySelectorAll('[data-rdm-option-index]'));
    const variantIdInput = /** @type {HTMLInputElement | null} */ (formEl.querySelector('[data-rdm-variant-id-input]'));
    const addToCartBtn = /** @type {HTMLButtonElement | null} */ (formEl.querySelector('[data-rdm-add-to-cart]'));
    const comparePriceEl = /** @type {HTMLElement | null} */ (rdmContent.querySelector('[data-rdm-compare-price]'));
    const currentPriceEl = /** @type {HTMLElement | null} */ (rdmContent.querySelector('[data-rdm-current-price]'));
    const usdPriceEl = /** @type {HTMLElement | null} */ (rdmContent.querySelector('[data-rdm-usd-price]'));
    const mainImageEl = /** @type {HTMLImageElement | null} */ (rdmContent.querySelector('.rdm-product__main-image'));
    const activeCurrency = metadata?.activeCurrency || 'EUR';
    const dopToUsdRate = metadata?.dopToUsdRate || 0.017;

    const formatMoney = (cents) => {
      const currency = activeCurrency || 'EUR';
      const locale = currency === 'DOP' ? 'es-DO' : 'es-ES';
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format((Number(cents) || 0) / 100);
    };

    const selectedValues = () =>
      optionEls.map((selectEl) => ({
        idx: Number(selectEl.getAttribute('data-rdm-option-index')),
        value: /** @type {HTMLSelectElement} */ (selectEl).value,
      }));

    const findVariant = () => {
      if (!productData?.variants?.length) return null;
      const opts = selectedValues();
      return productData.variants.find((variant) => opts.every(({ idx, value }) => variant.options[idx] === value)) || null;
    };

    const setAddButtonState = (variant) => {
      if (!addToCartBtn) return;
      const available = Boolean(variant?.available);
      addToCartBtn.disabled = !available;
      addToCartBtn.textContent = available ? 'Anadir a la cesta' : 'Variante sin stock';
    };

    const setPriceState = (variant) => {
      if (!currentPriceEl || !variant) return;
      currentPriceEl.textContent = formatMoney(variant.price);

      if (usdPriceEl) {
        const usdAmount = ((variant.price || 0) / 100) * dopToUsdRate;
        usdPriceEl.textContent = usdAmount.toFixed(2);
      }

      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = formatMoney(variant.compare_at_price);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }
    };

    const setImageState = (variant) => {
      if (!mainImageEl || !variant) return;
      const media = variant.featured_media || variant.featured_image;
      const src = media?.src || media?.url || media?.preview_image?.src;
      if (src) {
        mainImageEl.src = src;
      }
    };

    const syncVariant = () => {
      const variant = findVariant();
      if (!variant) {
        setAddButtonState(null);
        return null;
      }
      if (variantIdInput) {
        variantIdInput.value = String(variant.id);
      }
      setAddButtonState(variant);
      setPriceState(variant);
      setImageState(variant);
      return variant;
    };

    const bindHeveaGroup = (groupKey) => {
      const nativeSelect = /** @type {HTMLSelectElement | null} */ (rdmContent.querySelector(`[data-hevea-native="${groupKey}"]`));
      const selectedValueNode = rdmContent.querySelector(`[data-hevea-selected="${groupKey}"]`);
      const visualOptions = Array.from(rdmContent.querySelectorAll(`[data-hevea-group="${groupKey}"] [data-hevea-value]`));
      if (!nativeSelect || visualOptions.length === 0) return;

      const setValue = (value) => {
        nativeSelect.value = value;
        nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        visualOptions.forEach((node) => {
          const isActive = node.getAttribute('data-hevea-value') === value;
          node.classList.toggle('is-active', isActive);
          node.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        if (selectedValueNode) selectedValueNode.textContent = value;
      };

      visualOptions.forEach((node) => {
        node.setAttribute('aria-selected', 'false');
        node.addEventListener('click', () => {
          const value = node.getAttribute('data-hevea-value') || '';
          setValue(value);
        });
      });

      setValue(nativeSelect.value || visualOptions[0].getAttribute('data-hevea-value') || '');
    };

    bindHeveaGroup('estructura');
    bindHeveaGroup('tapizado');
    bindHeveaGroup('cojines');

    const coverSelect = /** @type {HTMLSelectElement | null} */ (
      rdmContent.querySelector('[id^="rdm-cover-"]') ||
        Array.from(rdmContent.querySelectorAll('[data-rdm-option-name]')).find((selectEl) =>
          (selectEl.getAttribute('data-rdm-option-name') || '').toLowerCase().includes('funda'),
        )
    );
    const coverTitle = rdmContent.querySelector('[data-hevea-cover-title]');
    const coverNote = rdmContent.querySelector('[data-hevea-cover-note]');
    const coverMessages = {
      No: {
        title: 'Sin funda',
        note: 'Sin recargo. Recomendado solo si el mueble se guarda protegido.',
      },
      Alpha: {
        title: 'Funda Alpha',
        note: 'Proteccion estandar para polvo, rocio y uso ocasional en exterior. Con recargo.',
      },
      Gamma: {
        title: 'Funda Gamma',
        note: 'Proteccion reforzada para uso intenso y largas temporadas. Con recargo superior.',
      },
    };
    if (coverSelect && coverTitle && coverNote) {
      const syncCoverInfo = () => {
        const key = /** @type {keyof typeof coverMessages} */ (coverSelect.value || 'No');
        const message = coverMessages[key] || coverMessages.No;
        coverTitle.textContent = message.title;
        coverNote.textContent = message.note;
      };
      coverSelect.addEventListener('change', syncCoverInfo);
      syncCoverInfo();
    }

    optionEls.forEach((selectEl) => {
      selectEl.addEventListener('change', syncVariant);
      selectEl.addEventListener('input', syncVariant);
    });
    syncVariant();

    formEl.addEventListener('submit', async (event) => {
      event.preventDefault();
      const currentVariant = syncVariant();
      if (!currentVariant?.available) return;

      if (addToCartBtn) {
        addToCartBtn.disabled = true;
      }

      try {
        const formData = new FormData(formEl);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Failed add to cart: ${response.status}`);
        }

        const cartResponse = await fetch('/cart.js', {
          headers: {
            Accept: 'application/json',
          },
        });
        const cart = cartResponse.ok ? await cartResponse.json() : null;

        document.dispatchEvent(
          new CartUpdateEvent(cart, 'quick-add-rdm', {
            didError: false,
            source: 'quick-add-rdm',
            variantId: String(currentVariant.id),
          }),
        );
      } catch (_error) {
        document.dispatchEvent(
          new CartUpdateEvent(null, 'quick-add-rdm', {
            didError: true,
            source: 'quick-add-rdm',
          }),
        );
      } finally {
        if (addToCartBtn) {
          addToCartBtn.disabled = false;
        }
      }
    });
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
